import React from 'react'
import { Box, Typography, Card, Button, Chip, Stack, Grid, CircularProgress, useTheme, useMediaQuery } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { ActiveSubscription, BillingPlan } from '../../services/api'
import { formatDateDDMMYYYY } from '../../utils/dateUtils'
import { formatINR, extractPlanFeatureItems } from '../../utils/paymentUtils'

export interface ActiveSubscriptionCardProps {
  activeSubscription: ActiveSubscription | null
  isEssentials: boolean
  hasGrowthSubscription: boolean
  plans?: BillingPlan[]
  onUpgradeClick: (planType: string) => void
  onCancelClick: (subscriptionId?: string) => void
  loading?: boolean
}

export const ActiveSubscriptionCard: React.FC<ActiveSubscriptionCardProps> = ({
  activeSubscription,
  isEssentials,
  hasGrowthSubscription,
  plans,
  onUpgradeClick,
  onCancelClick,
  loading = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const currentPlan = activeSubscription?.billing_plan || null

  const growthPlan = plans?.find(
    (p) => p.display_metadata?.mode === 'featured' || p.plan_type === 'growth' || p.sku === 'growth_monthly'
  )

  const planName = currentPlan?.display_metadata?.title || activeSubscription?.plan_type
  const priceText = currentPlan ? formatINR((currentPlan.amount_subunits || 0) / 100) : ''
  const targetGrowthPlanType = growthPlan?.plan_type || 'growth'

  const featureItems = extractPlanFeatureItems(currentPlan).filter((item) => item.included)

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: '#e2e8f0', borderRadius: 4, p: { xs: 2.5, sm: 3.5 }, mb: 4, bgcolor: '#ffffff' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            Current Plan
          </Typography>
          <Typography variant="caption" color="#64748b" sx={{ display: 'block', fontSize: isMobile ? '0.775rem' : '0.875rem', lineHeight: 1.4 }}>
            Manage your subscription
          </Typography>
        </Box>
        {isEssentials && !hasGrowthSubscription && (
          <Button
            variant="contained"
            size="small"
            disabled={loading}
            onClick={() => onUpgradeClick(targetGrowthPlanType)}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: 2.5,
              fontWeight: 800,
              px: 2,
              py: 1,
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none',
              fontSize: '0.875rem',
              background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)',
              boxShadow: '0 2px 4px rgba(37, 99, 235, 0.15)',
            }}
          >
            ⚡ Upgrade to {growthPlan?.display_metadata?.title || 'Growth Plan'}
          </Button>
        )}
      </Box>

      {activeSubscription ? (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Box>
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                  {planName}
                </Typography>
                <Chip
                  label={activeSubscription?.status === 'cancel_scheduled' ? 'CANCELLATION SCHEDULED' : 'ACTIVE'}
                  color={activeSubscription?.status === 'cancel_scheduled' ? 'warning' : 'success'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 22,
                    letterSpacing: '0.03em',
                  }}
                />
              </Stack>
              <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                {activeSubscription?.status === 'cancel_scheduled'
                  ? `Plan ends on: ${activeSubscription?.ends_at ? formatDateDDMMYYYY(activeSubscription.ends_at) : 'End of cycle'}`
                  : `Renews on: ${activeSubscription?.ends_at ? formatDateDDMMYYYY(activeSubscription.ends_at) : 'Active'}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" fontWeight={800} color="#0f172a">
                {priceText} <Typography component="span" variant="body2" color="#64748b" fontWeight={500}>/ month</Typography>
              </Typography>
              {activeSubscription?.status === 'active' && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => onCancelClick(activeSubscription?.razorpay_subscription_id || undefined)}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.825rem' }}
                >
                  Cancel Plan
                </Button>
              )}
            </Box>
          </Box>

          {featureItems.length > 0 && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px dashed', borderColor: '#e2e8f0' }}>
              <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1.5 }}>
                {currentPlan?.display_metadata?.features_header || "What's included in your plan:"}
              </Typography>
              <Grid container spacing={2}>
                {featureItems.map((item, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.2, flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        color={item.highlight ? '#0f172a' : '#475569'}
                        fontWeight={item.highlight ? 700 : 500}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {item.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
            No Active Subscription
          </Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
            You don't have an active plan right now. Choose a plan below to unlock automated AI replies and campaign broadcasts.
          </Typography>
        </Box>
      )}
    </Card>
  )
}
